import { action, computed, observable } from "mobx";
import { GetPublishSubcontractorInvoiceStatus, WorkflowStatus } from "@graphql";

export type WorkflowTask = {
  workflowId: string;
  title: string;
  bookingId?: string;
  createdAt: Date;
  updatedAt: Date;
  status: WorkflowStatus;
  errorMessage: string | null;
  warning: string | null;
  metadata?: Record<string, any> | null;
};

const NORMAL_POLL_MS = 5_000;
const SLOW_POLL_MS = 15_000;
const STALE_AFTER_MS = 2 * 60_000;
const STALE_WARNING =
  "Arbetet tar längre tid än vanligt — det fortsätter i bakgrunden.";

const isInFlight = (status: WorkflowStatus) =>
  status === WorkflowStatus.Scheduled || status === WorkflowStatus.Running;

export class WorkflowTasksStore {
  @observable private tasks: WorkflowTask[] = [];
  private pollingHandle: ReturnType<typeof setInterval> | null = null;
  private currentPollMs: number | null = null;
  private isPolling = false;

  @computed get items() {
    return this.tasks;
  }

  @computed get hasItems() {
    return this.tasks.length > 0;
  }

  @action.bound addTask(task: { workflowId: string; title: string; bookingId?: string; metadata?: Record<string, any> | null }) {
    const existing = this.tasks.find((x) => x.workflowId === task.workflowId);
    const now = new Date();

    if (existing) {
      existing.title = task.title;
      existing.bookingId = task.bookingId;
      existing.status = WorkflowStatus.Scheduled;
      existing.errorMessage = null;
      existing.warning = null;
      existing.updatedAt = now;
      existing.createdAt = now;
      existing.metadata = task.metadata ?? null;
    } else {
      this.tasks.push({
        workflowId: task.workflowId,
        title: task.title,
        bookingId: task.bookingId,
        createdAt: now,
        updatedAt: now,
        status: WorkflowStatus.Scheduled,
        errorMessage: null,
        warning: null,
        metadata: task.metadata ?? null
      });
    }

    this.ensurePolling();
  }

  @action.bound closeTask(workflowId: string) {
    this.tasks = this.tasks.filter((x) => x.workflowId !== workflowId);
    this.ensurePolling();
  }

  private hasInFlightTasks() {
    return this.tasks.some((x) => isInFlight(x.status));
  }

  private ensurePolling() {
    if (!this.hasInFlightTasks()) {
      if (this.pollingHandle) {
        clearInterval(this.pollingHandle);
        this.pollingHandle = null;
        this.currentPollMs = null;
      }
      return;
    }

    const anyStale = this.tasks.some((x) => x.warning);
    const targetMs = anyStale ? SLOW_POLL_MS : NORMAL_POLL_MS;

    if (this.currentPollMs === targetMs) {
      return;
    }

    const wasNotPolling = this.pollingHandle === null;

    if (this.pollingHandle) {
      clearInterval(this.pollingHandle);
    }
    this.pollingHandle = setInterval(() => this.pollRunningTasks(), targetMs);
    this.currentPollMs = targetMs;

    if (wasNotPolling) {
      this.pollRunningTasks();
    }
  }

  private async pollRunningTasks() {
    if (this.isPolling) {
      return;
    }

    const inFlightTasks = this.tasks.filter((x) => isInFlight(x.status));

    if (inFlightTasks.length === 0) {
      this.ensurePolling();
      return;
    }

    this.isPolling = true;

    try {
      await Promise.all(
        inFlightTasks.map(async (task) => {
          try {
            const run = await GetPublishSubcontractorInvoiceStatus.func(
              { workflowId: task.workflowId },
              { fetchPolicy: "no-cache" }
            );

            const workflowRun = run?.data?.workflowRunByWorkflowId;

            if (workflowRun) {
              this.updateTaskStatus(
                task.workflowId,
                workflowRun.status,
                workflowRun.errorMessage
              );
            }
          } catch (e) {
            console.warn(`Polling failed for ${task.workflowId}`, e);
          }

          this.maybeMarkStale(task.workflowId);
        })
      );
    } finally {
      this.isPolling = false;
      this.ensurePolling();
    }
  }

  @action.bound
  private updateTaskStatus(
    workflowId: string,
    status: WorkflowStatus,
    errorMessage: string | null
  ) {
    const item = this.tasks.find((x) => x.workflowId === workflowId);

    if (!item) {
      return;
    }

    item.status = status;
    item.errorMessage = errorMessage || null;
    item.updatedAt = new Date();

    if (!isInFlight(status)) {
      item.warning = null;
    }
  }

  @action.bound
  private maybeMarkStale(workflowId: string) {
    const item = this.tasks.find((x) => x.workflowId === workflowId);

    if (!item || item.warning) {
      return;
    }

    if (
      item.status === WorkflowStatus.Scheduled &&
      Date.now() - item.createdAt.getTime() > STALE_AFTER_MS
    ) {
      item.warning = STALE_WARNING;
    }
  }
}
